import React, { useMemo } from 'react'
import { CreditApplicationStatus } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'

import {
  GetCreditApplicationDetails_creditApplication_CreditApplication_requiredDocs_CreditApplicationDocument,
  useGetCreditApplicationDetails,
} from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/GetCreditApplicationDetails.gql'

import FileUploadSection from './FileUploadSection'
import OtherDocumentsSection from './OtherDocumentsSection'
import RequiredDocumentsSection from './RequiredDocumentsSection'
import styles from './styles.module.scss'

const canUploadFiles = (status: CreditApplicationStatus | null) => {
  switch (status) {
    case null:
    case CreditApplicationStatus.REJECTED:
    case CreditApplicationStatus.COMPANY_BUILT:
    case CreditApplicationStatus.APPROVED:
    case CreditApplicationStatus.EXPIRED:
    case CreditApplicationStatus.CUSTOMER_CANCELED:
    case CreditApplicationStatus.BUILD_ACCOUNT:
    case CreditApplicationStatus.CLOSED_AS_DUPLICATE:
    case CreditApplicationStatus.CLOSED_LOST:
    case CreditApplicationStatus.NOT_STARTED:
      return false
    default:
      return true
  }
}

type CreditApplicationDocument =
  GetCreditApplicationDetails_creditApplication_CreditApplication_requiredDocs_CreditApplicationDocument

interface DocumentsTabContentProps {
  salesforceCreditId: string
}

const DocumentsTabContent = ({ salesforceCreditId }: DocumentsTabContentProps) => {
  const [getClassName, makeTestId] = useNameFormatter('DocumentsTabContent')

  const { data } = useGetCreditApplicationDetails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
  })

  const { status } = data?.creditApplication ?? {}

  const supportingDocuments: CreditApplicationDocument[] = useMemo(() => {
    if (!data?.creditApplication?.supportingDocuments?.length) {
      return []
    }

    return (
      data.creditApplication.supportingDocuments.filter((doc): doc is CreditApplicationDocument => doc !== null) ?? []
    )
  }, [data])

  const requiredDocuments: CreditApplicationDocument[] = useMemo(() => {
    if (!data?.creditApplication?.requiredDocs?.length) {
      return []
    }

    return data.creditApplication.requiredDocs.filter((doc): doc is CreditApplicationDocument => doc !== null) ?? []
  }, [data])

  const otherDocuments: CreditApplicationDocument[] = useMemo(() => {
    return supportingDocuments.filter(
      ({ documentUuid }) => !requiredDocuments.some(({ documentUuid: reqDocId }) => reqDocId === documentUuid),
    )
  }, [supportingDocuments, requiredDocuments])

  return (
    <div
      className={styles[getClassName('')]}
      data-testid={makeTestId('')}
    >
      {canUploadFiles(status ?? null) && <FileUploadSection salesforceCreditId={salesforceCreditId} />}
      {requiredDocuments.length > 0 && (
        <RequiredDocumentsSection
          requiredDocuments={requiredDocuments}
          salesforceCreditId={salesforceCreditId}
        />
      )}
      {otherDocuments.length > 0 && (
        <OtherDocumentsSection
          documents={otherDocuments}
          hasRequiredDocuments={requiredDocuments.length > 0}
          salesforceCreditId={salesforceCreditId}
        />
      )}
    </div>
  )
}

export default DocumentsTabContent
